import type { KyInstance } from 'ky';
import { z } from 'zod';

// --- Zod Schemas ---

const loginChallengeResponseSchema = z.object({
    token: z.string(),
    challenge: z.string(),
});

const loginAuthResponseSchema = z.object({
    token: z.string(),
    expires: z.number(),
});

// --- Types ---

export type LoginChallengeResult = z.infer<typeof loginChallengeResponseSchema>;
export type LoginAuthResult = z.infer<typeof loginAuthResponseSchema>;

// --- Service Functions ---

/**
 * Step 1 of the login flow: request a challenge from the backend.
 *
 * Sends `POST /login` with `{ wallet }` and receives a challenge
 * string that must be signed by the user's wallet.
 *
 * @param api - Unauthenticated API client (wolfswapApi)
 * @param wallet - The user's wallet address
 * @returns The challenge token and challenge message to sign
 */
export async function requestLoginChallenge(
    api: KyInstance,
    wallet: string,
): Promise<LoginChallengeResult> {
    const data = await api
        .post('login', {
            json: { wallet },
        })
        .json();

    return loginChallengeResponseSchema.parse(data);
}

/**
 * Step 2 of the login flow: submit the signed challenge.
 *
 * Sends `POST /login` with `{ wallet, token, data }` and receives
 * the JWT and its expiry time (in seconds).
 *
 * @param api - Unauthenticated API client (wolfswapApi)
 * @param wallet - The user's wallet address
 * @param token - The challenge token from step 1
 * @param data - The signed challenge (hex signature from personal_sign)
 * @returns The JWT token and expiry duration in seconds
 */
export async function submitLoginSignature(
    api: KyInstance,
    wallet: string,
    token: string,
    data: string,
): Promise<LoginAuthResult> {
    const responseData = await api
        .post('login', {
            json: { wallet, token, data },
        })
        .json();

    return loginAuthResponseSchema.parse(responseData);
}
